import { useEffect, useState } from "react";
import { AlertTriangle, Loader2, Trash2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { deleteImage, getImageUsage } from "@/api";

/**
 * Confirms image deletion. Always fetches /usage when opened.
 * If used elsewhere, surfaces a list and offers a cascade option.
 */
export default function ConfirmDeleteImageDialog({
  image,
  open,
  onOpenChange,
  onDeleted,
}) {
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [working, setWorking] = useState(false);

  useEffect(() => {
    let active = true;
    if (open && image) {
      setUsage(null);
      setLoading(true);
      getImageUsage(image.id)
        .then((u) => {
          if (active) setUsage(u);
        })
        .catch(() => {
          if (active) setUsage({ total: 0 });
        })
        .finally(() => {
          if (active) setLoading(false);
        });
    }
    return () => {
      active = false;
    };
  }, [open, image]);

  async function performDelete(cascade) {
    if (!image) return;
    setWorking(true);
    try {
      await deleteImage(image.id, cascade);
      onDeleted?.(image);
      onOpenChange?.(false);
      toast.success(
        cascade ? "Image and all references removed" : "Image deleted"
      );
    } catch (e) {
      console.error(e);
      toast.error("Delete failed");
    } finally {
      setWorking(false);
    }
  }

  if (!image) return null;
  const total = usage?.total ?? 0;
  const usedElsewhere = total > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="confirm-delete-image-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            {usedElsewhere && <AlertTriangle className="w-5 h-5 text-amber-600" />}
            Delete image?
          </DialogTitle>
          <DialogDescription className="text-stone-600">
            {loading
              ? "Checking where this image is used…"
              : usedElsewhere
              ? "This image is referenced in other places in the project."
              : "This will permanently remove the image from your library."}
          </DialogDescription>
        </DialogHeader>

        {!loading && usedElsewhere && (
          <div
            className="space-y-2 text-sm text-stone-700 bg-amber-50 border border-amber-200 rounded-lg p-3 max-h-56 overflow-y-auto"
            data-testid="image-usage-list"
          >
            {usage.moodboards?.length > 0 && (
              <UsageBlock
                title="Moodboards"
                items={usage.moodboards.map(
                  (m) => `${m.name} (${m.count}×)`
                )}
              />
            )}
            {usage.floorplan_base?.length > 0 && (
              <UsageBlock
                title="Base floorplan image of"
                items={usage.floorplan_base.map((f) => f.name)}
                emphasis="Will delete the floorplan too."
              />
            )}
            {usage.floorplan_pins?.length > 0 && (
              <UsageBlock
                title="Pinned on floorplans"
                items={usage.floorplan_pins.map(
                  (f) => `${f.name} (${f.count} pin${f.count === 1 ? "" : "s"})`
                )}
              />
            )}
            {usage.products?.length > 0 && (
              <UsageBlock
                title="Products"
                items={usage.products.map((p) => p.name)}
              />
            )}
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange?.(false)}
            disabled={working}
            className="rounded-full"
            data-testid="delete-image-cancel"
          >
            Cancel
          </Button>
          {!usedElsewhere ? (
            <Button
              onClick={() => performDelete(false)}
              disabled={loading || working}
              className="rounded-full bg-red-600 hover:bg-red-700 text-white"
              data-testid="delete-image-confirm"
            >
              {working ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" strokeWidth={1.5} />
              )}
              Delete
            </Button>
          ) : (
            <>
              <Button
                onClick={() => performDelete(false)}
                disabled={working}
                variant="outline"
                className="rounded-full"
                data-testid="delete-image-library-only"
              >
                Library only
              </Button>
              <Button
                onClick={() => performDelete(true)}
                disabled={working}
                className="rounded-full bg-red-600 hover:bg-red-700 text-white"
                data-testid="delete-image-cascade"
              >
                {working ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4 mr-2" strokeWidth={1.5} />
                )}
                Remove from everywhere
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UsageBlock({ title, items, emphasis }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-[0.15em] font-semibold text-stone-500">
        {title}
      </div>
      <ul className="mt-1 space-y-0.5">
        {items.map((it, i) => (
          <li key={i} className="text-stone-800 truncate">
            • {it}
          </li>
        ))}
      </ul>
      {emphasis && (
        <p className="text-[11px] text-amber-700 mt-1 italic">{emphasis}</p>
      )}
    </div>
  );
}
